import { useState, useEffect } from "react";
import { adminGetAgents, adminCreateAgent, adminToggleAgent } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserPlus, Loader2, Copy, Users, UserCheck, TrendingUp, X, Search, Power, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/config/navigationConfig";

// Shows the onboarding source of an agent: self-registered, employee-referred, or admin-created.
function OnboardedByBadge({ agent }) {
  const type = agent?.onboarded_by_type;
  if (type === 'self') {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded text-[11px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
        Self-registered
      </span>
    );
  }
  if (type === 'employee') {
    const empName = agent.onboarded_by_name
      || agent.referred_by_employee?.name
      || 'Employee';
    const empCode = agent.employee_referral_code_used
      || agent.employee_referral_code
      || agent.referred_by_employee?.employee_id;
    return (
      <div>
        <span className="inline-flex items-center px-2 py-1 rounded text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
          Employee
        </span>
        <div className="mt-1 text-[11px] font-medium text-slate-700">{empName}</div>
        {empCode && (
          <code className="inline-block mt-0.5 text-[10px] font-mono text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded">
            {empCode}
          </code>
        )}
      </div>
    );
  }
  if (type === 'admin') {
    return (
      <div>
        <span className="inline-flex items-center px-2 py-1 rounded text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
          Admin
        </span>
        {agent.onboarded_by_name && (
          <div className="mt-1 text-[11px] text-slate-600">{agent.onboarded_by_name}</div>
        )}
      </div>
    );
  }
  // Legacy rows that weren't backfilled (shouldn't happen after migration, but safe fallback)
  if (agent?.referred_by_employee) {
    return (
      <div>
        <span className="inline-flex items-center px-2 py-1 rounded text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
          Employee
        </span>
        <div className="mt-1 text-[11px] font-medium text-slate-700">{agent.referred_by_employee.name}</div>
      </div>
    );
  }
  return <span className="text-xs text-gray-400">—</span>;
}

export default function AdminAgents() {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", password: "" });

  useEffect(() => { loadAgents(); }, []);

  const loadAgents = async () => {
    setLoading(true);
    try {
      const res = await adminGetAgents();
      setAgents(res.data.agents || []);
    } catch {} finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.error("Fill required fields"); return; }
    setCreating(true);
    try {
      const res = await adminCreateAgent(form);
      toast.success(`Agent created! Referral code: ${res.data.agent.referral_code}`);
      setForm({ name: "", phone: "", email: "", password: "" });
      setShowForm(false);
      loadAgents();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create agent"); }
    finally { setCreating(false); }
  };

  const handleToggle = async (id) => {
    try {
      await adminToggleAgent(id);
      toast.success("Agent status updated");
      loadAgents();
    } catch { toast.error("Failed"); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Referral code copied!");
  };

  const filtered = agents.filter(a => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (a.name || '').toLowerCase().includes(q) || (a.email || '').toLowerCase().includes(q) || (a.referral_code || '').toLowerCase().includes(q);
  });

  const stats = {
    total: agents.length,
    active: agents.filter(a => a.status === 'active').length,
    totalReferrals: agents.reduce((sum, a) => sum + (a.referred_count || 0), 0),
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-agents-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-black text-[#0A2540] tracking-tight" style={{fontFamily:'Outfit, sans-serif'}}>Agent Management</h2>
          <p className="text-sm text-[#6B7280] mt-1">Manage field agents and track referrals</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowForm(!showForm)} data-testid="create-agent-btn" className="bg-gradient-to-r from-[#1565C0] to-[#0D47A1] text-white hover:from-[#0D47A1] hover:to-[#0A3A8A] rounded-xl px-5 py-2.5 font-semibold shadow-sm hover:shadow-md transition-all">
            <UserPlus className="h-4 w-4 mr-2" /> Create Agent
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Agents', value: stats.total, icon: Users, color: '#3B82F6', bg: 'bg-blue-50' },
          { label: 'Active Agents', value: stats.active, icon: UserCheck, color: '#10B981', bg: 'bg-emerald-50' },
          { label: 'Total Referrals', value: stats.totalReferrals, icon: TrendingUp, color: '#8B5CF6', bg: 'bg-violet-50' },
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">{stat.label}</p>
                <p className="text-3xl font-black text-[#0A2540] mt-1" style={{fontFamily:'Outfit, sans-serif'}}>{stat.value}</p>
              </div>
              <div className={`${stat.bg} p-3 rounded-xl`}>
                <stat.icon className="h-6 w-6" style={{color: stat.color}} strokeWidth={1.8} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create Agent Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full shadow-2xl border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#0A2540]" style={{fontFamily:'Outfit, sans-serif'}}>Create New Agent</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 transition-colors"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Name *</Label>
                  <Input data-testid="agent-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1.5 rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0]" placeholder="Agent name" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Phone</Label>
                  <Input data-testid="agent-phone-input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1.5 rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0]" placeholder="Mobile number" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Email *</Label>
                  <Input data-testid="agent-email-create" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1.5 rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0]" placeholder="agent@email.com" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Password *</Label>
                  <Input data-testid="agent-password-create" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="mt-1.5 rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0]" placeholder="Set password" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="submit" data-testid="submit-create-agent" disabled={creating} className="flex-1 bg-gradient-to-r from-[#1565C0] to-[#0D47A1] text-white rounded-xl h-11 font-semibold shadow-sm hover:shadow-md transition-all">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Agent"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-xl h-11">Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search agents by name, email, or referral code..." className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0] focus:outline-none focus:bg-white transition-all" />
        </div>
      </div>

      {/* Agents Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-3 border-[#1565C0]/20 border-t-[#1565C0] rounded-full animate-spin" style={{borderWidth: '3px'}} />
              <p className="text-sm text-[#6B7280]">Loading agents...</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="agents-table">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-50/80 border-b border-gray-100">
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Agent</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Referral Code</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Onboarded By</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Referrals</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Status</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => (
                  <tr key={a.id} className="border-b border-gray-50 hover:bg-blue-50/30 transition-colors duration-150 group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm shadow-sm">
                          {(a.name || 'A')[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-[#0A2540]">{a.name}</p>
                          <p className="text-xs text-[#6B7280]">{a.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="inline-flex items-center gap-2">
                        <code className="bg-blue-50 text-[#1565C0] px-3 py-1.5 rounded-lg text-xs font-mono font-bold border border-blue-100">{a.referral_code}</code>
                        <button onClick={() => copyCode(a.referral_code)} className="text-gray-400 hover:text-[#1565C0] transition-colors"><Copy className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-testid={`agent-onboarded-by-${a.id}`}>
                      <OnboardedByBadge agent={a} />
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-lg font-black text-[#0A2540]" style={{fontFamily:'Outfit, sans-serif'}}>{a.referred_count || 0}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${a.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${a.status === 'active' ? 'bg-emerald-400' : 'bg-slate-400'}`}></span>
                        {a.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {isAdmin ? (
                        <button onClick={() => handleToggle(a.id)} data-testid={`toggle-agent-${a.id}`}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${a.status === 'active' ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100' : 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100'}`}>
                          <Power className="h-3 w-3" />
                          {a.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                          <Lock className="h-2.5 w-2.5" /> admin only
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-16">
                    <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-[#6B7280] font-medium">No agents found</p>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
